import { Box, VStack } from '@cdl/heartbeat-react';
import { useParams } from 'react-router-dom';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { ErrorBoundary } from '../../../shared/error-boundary/error-boundary';
import { useUndo } from '../../../shared/hooks/useUndo';
import { useTitle } from '../../../shared/hooks/useTitle';
import { onsetsLoadedSelector } from '../../scatter/selectors/scatter.selectors';
import {
  ecgIdSelector,
  holterStripLoadedSelector,
} from '../selectors/ecg.selectors';
import { signalLoadedSelector } from '../selectors/signal.selectors';
import { Delineation } from './Delineation';
import { DelineationHeader } from './DelineationHeader';
import { Loader } from './Loader';
import { Sidebar } from './Sidebar';
import { annotationEcgIdSelector } from '../selectors/annotation-task.selectors';
import { retrieveLatestAnnotation } from '../thunks/retrieve-latest-annotation.thunk';
import {
  ILRImageLoadedSelector,
  getLayoutValueSelector,
} from '../../layout/selectors/layout.selectors';
import { DelineationPickerSection } from '../../delineation/components/DelineationPickerSection';
import { DiagnosticEditor } from '../../diagnostic/components/DiagnosticEditor';
import { DiagnosticList } from '../../diagnostic/components/DiagnosticList';
import { useUnsavedChanged } from '../../../shared/hooks/useUnsavedChanges';
import { SwitchTaskInfo } from './SwitchTaskInfo';
import { HEADER_HEIGHT } from './Page';
import { AnnotationGridChecker } from './AnnotationGridChecker';
import { AutoLabelsEditor } from 'features/autoLabels/components/AutoLabelsEditor';
import { setRestingLayout } from '../../autoLabels/thunks/set-diagnostic-dropdown-layout.thunk';

const loadedSelector = createSelector(
  signalLoadedSelector,
  onsetsLoadedSelector,
  holterStripLoadedSelector,
  ILRImageLoadedSelector,
  (signalLoaded, onsetsLoaded, holterStripLoaded, hasLoaded) =>
    signalLoaded && onsetsLoaded && holterStripLoaded && hasLoaded,
);

export const EcgAnalysis: React.FC = () => {
  const { ecgId } = useParams();
  const dispatch = useDispatch();
  useUndo();
  useTitle(`Platane - ${ecgId}`);
  const currentEcgId = useSelector(ecgIdSelector);
  const loaded = useSelector(loadedSelector);
  const annotationEcgId = useSelector(annotationEcgIdSelector);
  const withSuggestionsPanel = useSelector(
    getLayoutValueSelector('withSuggestionsPanel'),
  );
  useUnsavedChanged();

  if (ecgId == null) {
    return <Box m='m'>This id is invalid</Box>;
  }

  if (annotationEcgId == null) {
    dispatch(retrieveLatestAnnotation(ecgId));
  }

  if (currentEcgId !== ecgId || !loaded) {
    return <Loader ecgId={ecgId} />;
  }

  dispatch(setRestingLayout());

  return (
    <ErrorBoundary>
      <SwitchTaskInfo>
        <AnnotationGridChecker>
          <DelineationHeader ecgId={ecgId} />
          <Delineation />
          <Sidebar>
            <VStack
              spacing='m'
              alignItems='stretch'
              height={`calc(100vh - ${HEADER_HEIGHT})`}
              p='m'
              overflow='auto'
            >
              <DelineationPickerSection />
              <DiagnosticEditor />
              {withSuggestionsPanel && <AutoLabelsEditor />}
              <DiagnosticList />
            </VStack>
          </Sidebar>
        </AnnotationGridChecker>
      </SwitchTaskInfo>
    </ErrorBoundary>
  );
};
